/**
 * Ticket hours helpers — display + validation only.
 *
 * total_hours is computed SERVER-SIDE (fieldiq-api src/lib/ticket-hours.ts) on
 * every write. The client must never compute or send it: a client number would
 * silently drift from the stored one. Everything here formats what the server
 * returned, or warns before a write the server will refuse to score.
 *
 * Server rule this mirrors:
 *   - end_date absent           → same-day wrap (end < start means overnight)
 *   - end_date === work_date
 *     AND end_time < start_time → total_hours NULL (contradiction, not a wrap).
 *     A 07:00 → 06:55 typo must not become 23.92 billable hours.
 */

import { calendarDayString } from './calendar-date';

// Both spellings of a stored day compare equal: '2026-04-12' and
// '2026-04-12 00:00:00+00' are the same work date (fe#815).
const toDay = (v: string | null | undefined): string =>
  calendarDayString(v) ?? (v ?? '');

const toMinutes = (v: string | null | undefined): number | null => {
  if (!v) return null;
  const [h, m] = v.split(':');
  const hours = parseInt(h, 10);
  const mins = parseInt(m ?? '0', 10);
  if (Number.isNaN(hours) || Number.isNaN(mins)) return null;
  return hours * 60 + mins;
};

/**
 * True when the entry is self-contradictory: an explicit finish date equal to
 * the arrival date, with a finish time earlier than the arrival time. The
 * server stores NULL hours for this — the UI should say so before saving.
 */
export function hasContradictoryTimes(
  workDate: string | null | undefined,
  endDate: string | null | undefined,
  startTime: string | null | undefined,
  endTime: string | null | undefined
): boolean {
  if (!endDate || toDay(endDate) !== toDay(workDate)) return false;
  const start = toMinutes(startTime);
  const end = toMinutes(endTime);
  if (start == null || end == null) return false;
  return end < start;
}

/**
 * Signature of the four columns total_hours is derived from, normalised so a
 * form value ("20:54") and a server value ("20:54:00") compare equal. Used to
 * tell whether the server's stored hours still describe what's on screen.
 */
export function ticketTimeKey(
  workDate: string | null | undefined,
  endDate: string | null | undefined,
  startTime: string | null | undefined,
  endTime: string | null | undefined
): string {
  const day = (v: string | null | undefined) => toDay(v) || '';
  const time = (v: string | null | undefined) => (v ?? '').slice(0, 5);
  return [day(workDate), day(endDate) || day(workDate), time(startTime), time(endTime)].join('|');
}

/**
 * Format the server's total_hours for display. Returns null when the server
 * has no answer — callers render an explanation, never 0 or NaN.
 */
export function formatTicketHours(raw: unknown): string | null {
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return n.toFixed(2);
}
